import { IPublicClientApplication } from '@azure/msal-browser';

/**
 * Helper to acquire an access token for API requests using MSAL.
 * Attempts silent acquisition, falls back to popup if needed.
 * The popup attempt is wrapped in a 15-second timeout so a browser-blocked
 * popup never causes the calling request to hang indefinitely.
 * Throws if no authenticated account is found.
 */
export async function getAccessTokenHelper(msalInstance: IPublicClientApplication, forceRefresh = false): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated account found');
  }
  const request = {
    scopes: [`${import.meta.env.VITE_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
    account: accounts[0],
    forceRefresh,
  };
  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch {
    // Fall back to interactive popup, but don't let a blocked popup hang forever.
    const POPUP_TIMEOUT_MS = 15_000;
    let popupTimeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<never>((_, reject) => {
      popupTimeoutId = setTimeout(
        // NOTE: acquireTokenPopup has no cancellation API, so the underlying popup window
        // stays open after this timeout fires. If the user completes sign-in in that orphaned
        // window, MSAL will silently populate its token cache. The next request will then
        // succeed via acquireTokenSilent without another popup. The error message below
        // reflects this: telling the user to retry their action (rather than refresh the
        // page) is intentional — a retry is likely to succeed if sign-in completed.
        () => reject(new Error('Sign-in timed out. Please close any open sign-in windows and try your request again.')),
        POPUP_TIMEOUT_MS
      );
    });
    try {
      const response = await Promise.race([
        msalInstance.acquireTokenPopup(request),
        timeoutPromise,
      ]);
      return response.accessToken;
    } finally {
      clearTimeout(popupTimeoutId!);
    }
  }
}
